/** Native-app website exits carry this explicit marker. It is not inferred from
 * the user agent: ordinary mobile Safari/Chrome visitors keep website consent. */
export type AppPlatform = "ios" | "android";
const PARAM = "fivestar_app";
const STORAGE_KEY = "fs_app_context";

function platform(value: string | null): AppPlatform | null {
  return value === "ios" || value === "android" ? value : null;
}

export function getAppContext(): AppPlatform | null {
  if (typeof window === "undefined") return null;
  const fromUrl = new URLSearchParams(window.location.search).getAll(PARAM)
    .map(platform).find(Boolean) ?? null;
  if (fromUrl) return fromUrl;
  try {
    return platform(window.sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function isAppContext(): boolean {
  return getAppContext() !== null;
}

/** Preserve the signal in every same-origin destination, including a reload
 * after SPA navigation when sessionStorage is blocked. */
export function withAppContext(destination: string): string {
  const app = getAppContext();
  if (!app || typeof window === "undefined") return destination;
  try {
    const url = new URL(destination, window.location.href);
    if (url.origin !== window.location.origin || !["http:", "https:"].includes(url.protocol)) {
      return destination;
    }
    url.searchParams.set(PARAM, app);
    return url.pathname + url.search + url.hash;
  } catch {
    return destination;
  }
}

let installed = false;
export function initializeAppContext(beforeAppNavigation?: () => void): void {
  if (installed || typeof window === "undefined") return;
  const app = getAppContext();
  installed = true;

  // An ordinary document may already have running analytics SDKs. Entering
  // app mode must discard that document, including for SPA/back navigation.
  const enterAppDocument = (url: URL, replace: boolean): boolean => {
    if (app || url.origin !== window.location.origin ||
      !url.searchParams.getAll(PARAM).some((value) => platform(value))) return false;
    try {
      beforeAppNavigation?.();
    } finally {
      if (replace) window.location.replace(url.href);
      else window.location.assign(url.href);
    }
    return true;
  };

  for (const method of ["pushState", "replaceState"] as const) {
    const original = history[method].bind(history);
    history[method] = ((state: unknown, unused: string, url?: string | URL | null) => {
      if (url != null && enterAppDocument(new URL(url, window.location.href), method === "replaceState")) return;
      original(state, unused, url == null ? url : withAppContext(String(url)));
    }) as History[typeof method];
  }
  window.addEventListener("popstate", () => enterAppDocument(new URL(window.location.href), true));

  if (!app) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, app);
  } catch {
    // URL propagation remains the durable path when storage is unavailable.
  }

  const updateAnchor = (anchor: HTMLAnchorElement) => {
    const href = anchor.getAttribute("href");
    if (!href) return;
    const marked = withAppContext(href);
    if (marked !== href) anchor.setAttribute("href", marked);
  };
  const updateAnchors = () => document.querySelectorAll("a[href]").forEach((a) => updateAnchor(a as HTMLAnchorElement));
  updateAnchors();
  new MutationObserver(updateAnchors).observe(document.documentElement, {
    childList: true, subtree: true, attributes: true, attributeFilter: ["href"],
  });
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (target instanceof Element) {
      const anchor = target.closest("a[href]");
      if (anchor) updateAnchor(anchor as HTMLAnchorElement);
    }
  }, true);
}
